import type { ButtonHTMLAttributes } from 'react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, variant = 'primary', size = 'md', isLoading, disabled, className = '', ...props }, ref) => {
    const { t } = useTranslation();
    const baseStyles =
      'inline-flex items-center justify-center rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

    const sizeStyles = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-3',
      lg: 'px-6 py-3 text-lg',
    };

    const variantStyles = {
      primary:
        'bg-primary-500 text-white hover:bg-primary-600 focus:ring-primary-500 disabled:hover:bg-primary-500',
      secondary:
        'bg-surface-raised text-content-primary hover:bg-surface-overlay focus:ring-gray-500 disabled:hover:bg-gray-600 dark:disabled:hover:bg-gray-700',
      outline:
        'border border-gray-300 dark:border-gray-600 text-content-secondary hover:bg-gray-100 dark:hover:bg-surface-overlay hover:text-content-primary focus:ring-gray-500 disabled:hover:bg-transparent dark:disabled:hover:bg-transparent',
      danger:
        'bg-red-600 dark:bg-red-700 text-white hover:bg-red-700 dark:hover:bg-red-600 focus:ring-red-500 disabled:hover:bg-red-600 dark:disabled:hover:bg-red-700',
    };

    return (
      <button
        ref={ref}
        disabled={disabled || isLoading}
        className={`${baseStyles} ${sizeStyles[size]} ${variantStyles[variant]} ${className}`}
        {...props}
      >
        {isLoading ? t('button.loading') : children}
      </button>
    );
  }
);

Button.displayName = 'Button';
